import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { toast } from '../../utils/toast';
import type { ProductEndpoint, EndpointTransformation } from '../../types';
import { ArrowRight, X, Plus } from 'lucide-react';

interface TransformationBuilderProps {
  productId: string;
  endpoints: ProductEndpoint[];
  selectedEndpoint: ProductEndpoint | null;
  onEndpointSelect: (endpoint: ProductEndpoint | null) => void;
}

type TransformationType = 'REQUEST' | 'RESPONSE';

export const TransformationBuilder: React.FC<TransformationBuilderProps> = ({
  endpoints,
  selectedEndpoint,
  onEndpointSelect,
}) => {
  const queryClient = useQueryClient();
  const [activeType, setActiveType] = useState<TransformationType>('REQUEST');
  const [mode, setMode] = useState<'transform' | 'passthrough'>('transform');
  const [fieldMappings, setFieldMappings] = useState<Array<{ source: string; target: string }>>([
    { source: '', target: '' },
  ]);
  const [constantInjections, setConstantInjections] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [testInput, setTestInput] = useState('{\n  "example": "value"\n}');
  const [testOutput, setTestOutput] = useState('');

  const [endpointSearch, setEndpointSearch] = useState('');
  const [showEndpointDropdown, setShowEndpointDropdown] = useState(false);

  useEffect(() => {
    if (selectedEndpoint) {
      setEndpointSearch(
        `${selectedEndpoint.public_method} ${selectedEndpoint.public_path} - ${selectedEndpoint.name}`
      );
    } else {
      setEndpointSearch('');
    }
  }, [selectedEndpoint]);

  const filteredEndpoints = React.useMemo(() => {
    const term = endpointSearch.toLowerCase();
    return endpoints.filter(
      (ep) =>
        ep.name.toLowerCase().includes(term) ||
        ep.public_path.toLowerCase().includes(term) ||
        ep.public_method.toLowerCase().includes(term)
    );
  }, [endpoints, endpointSearch]);

  const { data: transformations } = useQuery({
    queryKey: ['transformations', selectedEndpoint?.id],
    queryFn: async () => {
      if (!selectedEndpoint) {
        return [];
      }
      const response = await api.get<EndpointTransformation[]>(
        `/portal/admin/endpoints/${selectedEndpoint.id}/transformations`
      );
      return response.data;
    },
    enabled: !!selectedEndpoint,
  });

  useEffect(() => {
    if (transformations && transformations.length > 0) {
      const transformation = transformations.find((t) => t.type === activeType);
      if (transformation) {
        const rules = transformation.transformation_rules;
        setMode(rules.mode || 'transform');

        if (rules.field_mappings) {
          const mappings = Object.entries(rules.field_mappings).map(([source, target]) => ({
            source,
            target: target,
          }));
          setFieldMappings(mappings.length > 0 ? mappings : [{ source: '', target: '' }]);
        }

        if (rules.constant_injections) {
          const constants = Object.entries(rules.constant_injections).map(([key, value]) => ({
            key,
            value: JSON.stringify(value),
          }));
          setConstantInjections(constants);
        }
      }
    }
  }, [transformations, activeType]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEndpoint) {
        throw new Error('No endpoint selected');
      }

      const transformation_rules: any = {
        mode,
      };

      if (mode === 'transform') {
        const mappings: Record<string, string> = {};
        fieldMappings.forEach((m) => {
          if (m.source && m.target) {
            mappings[m.source] = m.target;
          }
        });
        if (Object.keys(mappings).length > 0) {
          transformation_rules.field_mappings = mappings;
        }

        if (constantInjections.length > 0) {
          const constants: Record<string, any> = {};
          constantInjections.forEach((c) => {
            if (c.key && c.value) {
              try {
                constants[c.key] = JSON.parse(c.value);
              } catch {
                constants[c.key] = c.value;
              }
            }
          });
          if (Object.keys(constants).length > 0) {
            transformation_rules.constant_injections = constants;
          }
        }
      }

      const existing = transformations?.find((t) => t.type === activeType);

      if (existing) {
        await api.put(`/portal/admin/transformations/${existing.id}`, {
          transformation_rules,
        });
      } else {
        await api.post(`/portal/admin/endpoints/${selectedEndpoint.id}/transformations`, {
          type: activeType,
          transformation_rules,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transformations', selectedEndpoint?.id] });
      toast.success('Transformation saved successfully!');
    },
    onError: (error) => {
      toast.error(`Failed to save transformation: ${getErrorMessage(error)}`);
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEndpoint) {
        throw new Error('No endpoint selected');
      }

      const transformation_rules: any = { mode };
      if (mode === 'transform') {
        const mappings: Record<string, string> = {};
        fieldMappings.forEach((m) => {
          if (m.source && m.target) {
            mappings[m.source] = m.target;
          }
        });
        transformation_rules.field_mappings = mappings;

        const constants: Record<string, any> = {};
        constantInjections.forEach((c) => {
          if (c.key && c.value) {
            try {
              constants[c.key] = JSON.parse(c.value);
            } catch {
              constants[c.key] = c.value;
            }
          }
        });
        transformation_rules.constant_injections = constants;
      }

      const response = await api.post(`/portal/admin/transformations/test`, {
        type: activeType,
        transformation_rules,
        test_input: JSON.parse(testInput),
      });
      return response.data;
    },
    onSuccess: (data) => {
      const output = data?.output ?? data?.data?.output ?? data;
      setTestOutput(JSON.stringify(output, null, 2));
    },
    onError: (error) => {
      setTestOutput(`Error: ${getErrorMessage(error)}`);
    },
  });

  const addFieldMapping = () => {
    setFieldMappings([...fieldMappings, { source: '', target: '' }]);
  };

  const removeFieldMapping = (index: number) => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== index));
  };

  const updateFieldMapping = (index: number, field: 'source' | 'target', value: string) => {
    const updated = [...fieldMappings];
    updated[index][field] = value;
    setFieldMappings(updated);
  };

  const addConstantInjection = () => {
    setConstantInjections([...constantInjections, { key: '', value: '' }]);
  };

  const removeConstantInjection = (index: number) => {
    setConstantInjections(constantInjections.filter((_, i) => i !== index));
  };

  const updateConstantInjection = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...constantInjections];
    updated[index][field] = value;
    setConstantInjections(updated);
  };

  return (
    <div className="space-y-6">
      {/* Endpoint Selector */}
      <div>
        <label className="label">Selected Endpoint</label>
        <div className="relative">
          <input
            type="text"
            className="input w-full"
            placeholder="Search endpoint by name or path..."
            value={endpointSearch}
            onChange={(e) => {
              setEndpointSearch(e.target.value);
              setShowEndpointDropdown(true);
              if (!e.target.value) onEndpointSelect(null);
            }}
            onFocus={() => setShowEndpointDropdown(true)}
            onBlur={() => setTimeout(() => setShowEndpointDropdown(false), 200)}
          />
          {showEndpointDropdown && (
            <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg max-h-60 rounded-xl py-1 text-base overflow-auto focus:outline-none sm:text-sm">
              {filteredEndpoints.length === 0 ? (
                <li className="text-slate-500 dark:text-slate-400 cursor-default select-none relative py-2 pl-3 pr-9">
                  Nenhum endpoint encontrado.
                </li>
              ) : (
                filteredEndpoints.map((endpoint) => (
                  <li
                    key={endpoint.id}
                    className="text-slate-900 dark:text-slate-200 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50 dark:hover:bg-primary-950/40"
                    onClick={() => {
                      onEndpointSelect(endpoint);
                      setEndpointSearch(
                        `${endpoint.public_method} ${endpoint.public_path} - ${endpoint.name}`
                      );
                      setShowEndpointDropdown(false);
                    }}
                  >
                    <div className="flex items-center">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-semibold rounded mr-2 ${
                          endpoint.public_method === 'GET'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400'
                            : endpoint.public_method === 'POST'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400'
                              : endpoint.public_method === 'PUT'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400'
                                : endpoint.public_method === 'DELETE'
                                  ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-400'
                                  : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                      >
                        {endpoint.public_method}
                      </span>
                      <span className="font-mono text-sm truncate mr-2">
                        {endpoint.public_path}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400 truncate">
                        - {endpoint.name}
                      </span>
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      {!selectedEndpoint ? (
        <div className="card text-center py-12">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Selecione um endpoint acima para configurar transformações.
          </p>
          {endpoints.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Nenhum endpoint disponível. Crie um endpoint primeiro.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Transformation Type Tabs */}
          <div className="border-b border-slate-200 dark:border-slate-800">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveType('REQUEST')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeType === 'REQUEST'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                Request Transformation
              </button>
              <button
                onClick={() => setActiveType('RESPONSE')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeType === 'RESPONSE'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                Response Transformation
              </button>
            </nav>
          </div>

          <div className="card">
            {/* Mode Selection */}
            <div className="mb-6">
              <label className="label">Transformation Mode</label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer text-slate-700 dark:text-slate-300">
                  <input
                    type="radio"
                    name="mode"
                    value="transform"
                    checked={mode === 'transform'}
                    onChange={(e) => setMode(e.target.value as 'transform' | 'passthrough')}
                    className="mr-2"
                  />
                  <span className="text-sm">Transform (apply mappings)</span>
                </label>
                <label className="flex items-center cursor-pointer text-slate-700 dark:text-slate-300">
                  <input
                    type="radio"
                    name="mode"
                    value="passthrough"
                    checked={mode === 'passthrough'}
                    onChange={(e) => setMode(e.target.value as 'transform' | 'passthrough')}
                    className="mr-2"
                  />
                  <span className="text-sm">Passthrough (no transformation)</span>
                </label>
              </div>
            </div>

            {mode === 'transform' && (
              <>
                {/* Field Mappings */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="label mb-0">Field Mappings</label>
                    <button
                      onClick={addFieldMapping}
                      className="btn-secondary text-sm inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Mapping</span>
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Use dot notation for nested fields (e.g., "user.address.city")
                  </p>
                  <div className="space-y-2">
                    {fieldMappings.map((mapping, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Source field (e.g., user.name)"
                          className="input flex-1"
                          value={mapping.source}
                          onChange={(e) => updateFieldMapping(index, 'source', e.target.value)}
                        />
                        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                          type="text"
                          placeholder="Target field (e.g., userName)"
                          className="input flex-1"
                          value={mapping.target}
                          onChange={(e) => updateFieldMapping(index, 'target', e.target.value)}
                        />
                        <button
                          onClick={() => removeFieldMapping(index)}
                          className="btn-danger text-sm p-2"
                          disabled={fieldMappings.length === 1}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Constant Injections */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="label mb-0">Constant Injections</label>
                    <button
                      onClick={addConstantInjection}
                      className="btn-secondary text-sm inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Constant</span>
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Add constant values to the transformed output
                  </p>
                  {constantInjections.length > 0 && (
                    <div className="space-y-2">
                      {constantInjections.map((constant, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <input
                            type="text"
                            placeholder="Key (e.g., sistemaOrigem)"
                            className="input flex-1"
                            value={constant.key}
                            onChange={(e) => updateConstantInjection(index, 'key', e.target.value)}
                          />
                          <span className="text-slate-400 font-bold">=</span>
                          <input
                            type="text"
                            placeholder='Value (e.g., "MARKETPLACE")'
                            className="input flex-1"
                            value={constant.value}
                            onChange={(e) =>
                              updateConstantInjection(index, 'value', e.target.value)
                            }
                          />
                          <button
                            onClick={() => removeConstantInjection(index)}
                            className="btn-danger text-sm p-2"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Test Transformation */}
            <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-3">
                Test Transformation
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Sample Input</label>
                  <textarea
                    className="textarea h-48 font-mono text-xs"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Transformed Output</label>
                  <textarea
                    className="textarea h-48 font-mono text-xs bg-slate-50 dark:bg-slate-800/80"
                    value={testOutput}
                    readOnly
                  />
                </div>
              </div>
              <button
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
                className="btn-secondary mt-3"
              >
                {testMutation.isPending ? 'Testing...' : 'Test Transformation'}
              </button>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6 mt-6 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                className="btn-primary"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Transformation'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
